// Auftrag-Detail Banner — zeigt Warnung wenn Beistelteile überfällig/fehlend sind
import { useEffect, useState } from 'react';
import { AlertTriangle, Clock, PackageCheck } from 'lucide-react';
import { api, type BeistelteilModul, type BeistelteilSummary } from '../lib/api';

type Props = {
  auftragModul: BeistelteilModul;
  auftragId: number;
};

export default function BeistelteileBanner({ auftragModul, auftragId }: Props) {
  const [s, setS] = useState<BeistelteilSummary | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.beistelteile.summary(auftragModul, [auftragId])
      .then(res => {
        if (cancelled) return;
        setS(res.data[String(auftragId)] ?? null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [auftragModul, auftragId]);

  if (!s || s.total === 0) return null;

  // Priorität: Überfällig (rot) > Erwartet (gelb) > Vollständig (grün)
  if (s.ueberfaellig > 0) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-center gap-3">
        <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
        <div className="text-sm">
          <span className="font-semibold text-red-900">{s.ueberfaellig} Beistelteil{s.ueberfaellig>1?'e':''} überfällig.</span>
          <span className="text-red-700 ml-1">
            Auftrag-Bearbeitung gefährdet — Lieferanten kontaktieren.
          </span>
        </div>
      </div>
    );
  }

  if (s.erwartet > 0) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-center gap-3">
        <Clock className="w-5 h-5 text-amber-600 flex-shrink-0" />
        <div className="text-sm">
          <span className="font-semibold text-amber-900">{s.erwartet} Beistelteil{s.erwartet>1?'e':''} erwartet.</span>
          <span className="text-amber-700 ml-1">
            Bearbeitung erst nach Eingang möglich.
          </span>
        </div>
      </div>
    );
  }

  if (s.eingegangen > 0 || s.verbaut > 0) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4 flex items-center gap-3">
        <PackageCheck className="w-5 h-5 text-emerald-600 flex-shrink-0" />
        <div className="text-sm">
          <span className="font-semibold text-emerald-900">
            Alle {s.total} Beistelteil{s.total>1?'e':''} eingegangen
            {s.verbaut > 0 ? ` (${s.verbaut} verbaut)` : ''}
          </span>
        </div>
      </div>
    );
  }

  return null;
}
